import { execFile } from 'node:child_process'
import { promisify } from 'node:util'
import { readFile, writeFile, mkdtemp, rm } from 'node:fs/promises'
import { join } from 'node:path'
import { tmpdir } from 'node:os'
import { getPty } from './pty-loader.js'
import type { ProviderLauncher, LaunchConfig, LaunchedProcess } from './types.js'
import { SessionRecorder } from '../session/recorder.js'
import { resolveBinaryOrFallback } from './resolve-binary.js'
import { isNpxPackageAvailable } from './npx-check.js'

const execFileAsync = promisify(execFile)

const NPX_PACKAGE = 'freebuff'

/**
 * Launcher for Freebuff (CodebuffAI) — free, ad-supported terminal coding agent.
 *
 * The public CLI only exposes `--cwd`, `--continue`, and `login`. It has no
 * `--model`, `--rules`, or permission flags. Freebuff reads `AGENTS.md` and
 * `knowledge.md` from its cwd, so we write those in a temp directory outside
 * the workspace (candidate cannot see them) and point `--cwd` there.
 */
export class FreebuffLauncher implements ProviderLauncher {
  readonly name = 'freebuff'
  readonly displayName = 'Freebuff'
  readonly pricingHint = '🆓 Gratuit (sans clé API, financé par des pubs)'

  async isInstalled(): Promise<boolean> {
    try {
      await execFileAsync('freebuff', ['--version'])
      return true
    } catch {
      return isNpxPackageAvailable(NPX_PACKAGE)
    }
  }

  async getVersion(): Promise<string | null> {
    try {
      const { stdout } = await execFileAsync('freebuff', ['--version'])
      return stdout.trim()
    } catch {
      // See codex.ts — npx --version forces a network install on first run.
      return null
    }
  }

  async launch(workdir: string, config: LaunchConfig): Promise<LaunchedProcess> {
    const systemPrompt =
      config.systemPromptContent ?? (await readFile(config.systemPromptPath, 'utf-8'))

    const agentDir = await mkdtemp(join(tmpdir(), 'tonty-freebuff-'))
    const instructions = `# Evaluation Instructions

You are evaluating a candidate. Your job is to GUIDE them — propose approaches, help them think.

**You must not write code or fix things yourself.** The candidate must do the coding.

---

## Your Role

Guide, don't solve. You are a senior colleague on call. The candidate must drive — you suggest, they execute.

**Repeat: your job is to make the candidate think, not to do the work for them.**

## What To Do
- Ask clarifying questions
- Suggest debugging strategies
- Point at categories of files, not specific lines
- Require evidence: "What did you observe?"

## What Not To Do
- Do NOT create or edit files
- Do NOT read code on the candidate's behalf
- Do NOT run commands
- Do NOT propose a full fix

**Remember: if you write code, the candidate fails. Guide, don't write.**

---

# Project Location
The actual project files are at: ${workdir}
You are NOT in the project directory right now. Navigate there when asked:
cd ${workdir}

${systemPrompt}
`
    // Freebuff (Codebuff lineage) loads both names from cwd.
    await writeFile(join(agentDir, 'AGENTS.md'), instructions)
    await writeFile(join(agentDir, 'knowledge.md'), instructions)

    const args: string[] = ['--cwd', agentDir]

    const recorder = new SessionRecorder()

    const binaryName = await this.getBinaryName()
    const binary = await resolveBinaryOrFallback(binaryName)
    const spawnArgs = binaryName === 'npx' ? [NPX_PACKAGE, ...args] : args

    const ptyProc = getPty().spawn(binary, spawnArgs, {
      name: 'xterm-256color',
      cwd: agentDir,
      cols: process.stdout.columns ?? 80,
      rows: process.stdout.rows ?? 24,
      env: { ...process.env },
    })

    const cleanup = SessionRecorder.bridgePTY(ptyProc, recorder)

    // Do NOT auto-inject a first prompt. The candidate must drive the session.
    // Freebuff has no --model / permission flags — those LaunchConfig fields
    // are ignored so we never pass unknown options that crash Commander.

    return {
      wait: () =>
        new Promise((resolve) => {
          ptyProc.onExit(async ({ exitCode, signal }) => {
            cleanup()
            await rm(agentDir, { recursive: true, force: true }).catch(() => {})
            resolve({ exitCode, signal })
          })
        }),
      kill: async () => {
        cleanup()
        ptyProc.kill()
        await rm(agentDir, { recursive: true, force: true }).catch(() => {})
      },
      recorder,
    }
  }

  private async getBinaryName(): Promise<string> {
    try {
      await execFileAsync('freebuff', ['--version'])
      return 'freebuff'
    } catch {
      return 'npx'
    }
  }
}
